import { isEmpty, isNonEmpty } from 'fp-ts/Array';
import { Option } from '@mews-commander/types';
import { LocalizedText } from '@mews/framework';
import { Banner, FormMessage, Stack, Status } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { SelectField } from '@mews-commander/form';
import { FIELD_NAMES } from '../types/Form';
import { WithEnterpriseIds } from '../../../../enterpriseRateManagement/types';
import { usePropertiesGroupsData } from '../../../queries';

type AddToExistingPropertyGroupFieldsProps = WithEnterpriseIds & {
    propertiesAlreadyInsideSelectedPropertyGroup: string[];
    noPropertyGroupsExist: boolean;
};

export const AddToExistingPropertyGroupFields = ({
    propertiesAlreadyInsideSelectedPropertyGroup,
    enterpriseIds,
    noPropertyGroupsExist,
}: AddToExistingPropertyGroupFieldsProps) => {
    const { data, isLoading } = usePropertiesGroupsData();

    const options: Option[] = data?.data
        ? data.data.map(({ id, name }) => ({
              value: id,
              label: name,
              disabled: false,
              inactive: false,
              indent: false,
              secondaryText: null,
          }))
        : [];

    return (
        <>
            <FormBanner
                propertiesAlreadyInsideSelectedPropertyGroup={propertiesAlreadyInsideSelectedPropertyGroup}
                enterpriseIds={enterpriseIds}
                noPropertyGroupsExist={noPropertyGroupsExist}
            />
            <Stack spacing="50" vertical>
                <SelectField
                    required
                    name={FIELD_NAMES.PROPERTY_GROUP_ID}
                    size="large"
                    loader={isLoading}
                    label={<LocalizedText textKey={TextKey.PropertyGroupsNavigation} />}
                    disabled={noPropertyGroupsExist}
                    helper={noPropertyGroupsExist ? <LocalizedText textKey={TextKey.NoPropertyGroupExistsYet} /> : null}
                    options={options}
                />
                <ErrorMessage
                    enterpriseIds={enterpriseIds}
                    propertiesAlreadyInsideSelectedPropertyGroup={propertiesAlreadyInsideSelectedPropertyGroup}
                />
            </Stack>
        </>
    );
};

interface ErrorMessageProps extends WithEnterpriseIds {
    propertiesAlreadyInsideSelectedPropertyGroup: string[];
}

const ErrorMessage = ({ enterpriseIds, propertiesAlreadyInsideSelectedPropertyGroup }: ErrorMessageProps) => {
    if (enterpriseIds.length === 1 && isNonEmpty(propertiesAlreadyInsideSelectedPropertyGroup)) {
        const errorMessageTextKey = TextKey.SelectedPropertyAlreadyPartOfPropertyGroup;

        return (
            <FormMessage status={Status.Error}>
                <LocalizedText textKey={errorMessageTextKey} />
            </FormMessage>
        );
    }

    if (enterpriseIds.length > 1 && enterpriseIds.length === propertiesAlreadyInsideSelectedPropertyGroup.length) {
        const errorMessageTextKey = TextKey.SelectedPropertiesAlreadyPartOfPropertyGroup;

        return (
            <FormMessage status={Status.Error}>
                <LocalizedText textKey={errorMessageTextKey} />
            </FormMessage>
        );
    }

    return null;
};

interface FormBannerProps extends WithEnterpriseIds {
    propertiesAlreadyInsideSelectedPropertyGroup: string[];
    noPropertyGroupsExist: boolean;
}

const FormBanner = ({
    enterpriseIds,
    noPropertyGroupsExist,
    propertiesAlreadyInsideSelectedPropertyGroup,
}: FormBannerProps) => {
    if (noPropertyGroupsExist) {
        return (
            <Banner
                variant="info"
                primaryText={<LocalizedText textKey={TextKey.NoPropertyGroupExistsYet} />}
                secondaryText={<LocalizedText textKey={TextKey.SelectCreateNewTabPrompt} />}
            />
        );
    }

    if (enterpriseIds.length > 1 && isEmpty(propertiesAlreadyInsideSelectedPropertyGroup)) {
        return (
            <Banner
                variant="info"
                primaryText={
                    <LocalizedText
                        textKey={TextKey.PropertiesWillBeAdded}
                        substitutes={{ count: enterpriseIds.length }}
                    />
                }
            />
        );
    }

    if (
        enterpriseIds.length > 1 &&
        isNonEmpty(propertiesAlreadyInsideSelectedPropertyGroup) &&
        enterpriseIds.length > propertiesAlreadyInsideSelectedPropertyGroup.length
    ) {
        return (
            <Banner
                variant="info"
                primaryText={
                    <LocalizedText
                        textKey={TextKey.PropertiesWillBeAdded}
                        substitutes={{
                            count: enterpriseIds.length - propertiesAlreadyInsideSelectedPropertyGroup.length,
                        }}
                    />
                }
                secondaryText={
                    <LocalizedText
                        textKey={TextKey.PropertiesAlreadyInPropertyGroup}
                        substitutes={{ count: propertiesAlreadyInsideSelectedPropertyGroup.length }}
                    />
                }
            />
        );
    }

    return null;
};
